import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import api from 'src/api/BlancoClient';
import {
  FortuneExchangeableItemsResponse,
  FortuneExchangeHistoryResponse,
  TodaysFortuneResponse,
  TodaysUpdatedFortuneResponse
} from 'src/api/response/fortune';
import i18n from 'src/i18n';
import { formatDate } from 'src/lib/dateFormatter';
import { getCommentId } from 'src/lib/fortuneCommentId';
import { FortuneExchangedItem } from 'src/models/fortune/FortuneExchangedItem';
import { FortuneItem } from 'src/models/fortune/FortuneItem';
import { UserProfile } from 'src/models/UserProfile';
import { RootState } from '.';
import { actions as userActions, getFreshToken, UserState } from './user';
import { checkErrorType } from './utils/helper';
import { RequestState } from './utils/RequestState';

type FortuneErrorType = 'loading-failed' | 'unstable-network' | 'unknown';
export type FortuneResultErrorType = FortuneErrorType | 'invalid-date' | 'have-already-got-point';
export type FortuneExchangeErrorType =
  | FortuneErrorType
  | 'lack-of-points'
  | 'exchange-invalid-item'
  | 'exceeded-number-of-changes'
  | 'invalid-date'
  | 'exceeded-exchanged-item-storage';

export interface FortuneResultState {
  currentPoint: number;
  today?: {
    point: number;
    commentId: string;
  };
  pointFactor?: number;
  userProfile?: UserProfile;
}

interface FortuneUpdateState {
  animationEnd: boolean;
}

interface FortuneExchangeHistory {
  totalUsedPoint: number;
  totalGotPoint: number;
  itemHistories: FortuneExchangedItem[];
}

export interface ExchangeableItemState {
  count: number;
  language: string;
  items: FortuneItem[];
}

export interface FortuneState {
  point?: RequestState<FortuneResultState, FortuneResultErrorType>;
  updateAborted?: boolean;
  updateStatus?: RequestState<FortuneUpdateState, FortuneResultErrorType>;
  exchangeableItem?: RequestState<ExchangeableItemState, FortuneErrorType>;
  exchangeHistory?: RequestState<FortuneExchangeHistory, FortuneErrorType>;
  exchangeStatus?: RequestState<Record<string, unknown>, FortuneExchangeErrorType>;
  displayedPoint?: number;
}

const initialState: FortuneState = {};

const slice = createSlice({
  name: 'fortune',
  initialState,
  reducers: {
    getTodaysFortune: state => {
      if (state.point?.state !== 'ok') {
        state.point = { state: 'pending' };
      }
    },
    getTodaysFortuneSucceeded: (state, { payload }: PayloadAction<FortuneResultState>) => {
      state.displayedPoint = payload.currentPoint;
      state.point = {
        state: 'ok',
        result: payload
      };
    },
    getTodaysFortuneFailed: (state, { payload }: PayloadAction<FortuneResultErrorType>) => {
      state.point = { state: 'error', error: payload };
    },

    updateTodaysFortune: state => {
      state.updateStatus = { state: 'pending' };
      state.updateAborted = false;
    },
    updateTodaysFortuneSucceeded: (
      state,
      {
        payload
      }: PayloadAction<{
        currentPoint: number;
        pointFactor: number;
        today: { point: number; commentId: string };
      }>
    ) => {
      if (state.point?.state === 'ok') {
        state.point.result.currentPoint = payload.currentPoint;
        state.point.result.pointFactor = payload.pointFactor;
        state.point.result.today = payload.today;

        state.updateStatus = {
          state: 'ok',
          result: { animationEnd: state.updateAborted ?? false }
        };
        if (state.updateAborted) {
          state.displayedPoint = state.point.result.currentPoint;
        }
      }
    },
    updateTodaysFortuneFailed: (state, { payload }: PayloadAction<FortuneResultErrorType>) => {
      state.updateStatus = { state: 'error', error: payload };
    },
    animationEnd: state => {
      if (state.point?.state === 'ok') {
        state.displayedPoint = state.point.result.currentPoint;

        if (state.updateStatus?.state === 'ok') {
          state.updateStatus.result.animationEnd = true;
        }
      }
    },

    getExchangeableItems: state => {
      if (state.exchangeableItem?.state !== 'ok') {
        state.exchangeableItem = { state: 'pending' };
      }
    },
    getExchangeableItemsSucceeded: (state, { payload }: PayloadAction<ExchangeableItemState>) => {
      state.exchangeableItem = { state: 'ok', result: payload };
    },
    getExchangeableItemsFailed: (state, { payload }: PayloadAction<FortuneErrorType>) => {
      state.exchangeableItem = { state: 'error', error: payload };
    },

    getFortuneExchangeHistory: state => {
      if (state.exchangeHistory?.state !== 'ok') {
        state.exchangeHistory = { state: 'pending' };
      }
    },
    getFortuneExchangeHistorySucceeded: (
      state,
      { payload }: PayloadAction<FortuneExchangeHistory>
    ) => {
      state.exchangeHistory = {
        state: 'ok',
        result: payload
      };
    },
    getFortuneExchangeHistoryFailed: (state, { payload }: PayloadAction<FortuneErrorType>) => {
      state.exchangeHistory = { state: 'error', error: payload };
    },

    exchangeFortuneItem: (
      state,
      _: PayloadAction<{
        unique_id: number;
        remake_id?: number;
      }>
    ) => {
      state.exchangeStatus = { state: 'pending' };
    },
    exchangeFortuneItemSucceeded: (state, { payload }: PayloadAction<{ currentPoint: number }>) => {
      state.exchangeStatus = { state: 'ok', result: {} };
      if (state.point?.state === 'ok') {
        state.point.result.currentPoint = payload.currentPoint;
      }
      state.displayedPoint = payload.currentPoint;
    },
    exchangeFortuneItemFailed: (state, { payload }: PayloadAction<FortuneExchangeErrorType>) => {
      state.exchangeStatus = { state: 'error', error: payload };
    },

    clearUpdateStatus: state => {
      // updateStatus が pending のときはクリアせず，updateAbortedフラグを立てる。
      if (state.updateStatus?.state === 'pending') {
        state.updateAborted = true;
      } else {
        state.updateAborted = false;
        state.updateStatus = undefined;
      }
    },

    resetExchangeStatus: state => {
      state.exchangeStatus = undefined;
    },

    reset: state => {
      state.point = undefined;
      state.updateStatus = undefined;
      state.exchangeHistory = undefined;
      state.exchangeableItem = undefined;
      state.displayedPoint = undefined;
    }
  }
});

export default slice.reducer;

const {
  getTodaysFortune,
  updateTodaysFortune,
  animationEnd,
  getExchangeableItems,
  getFortuneExchangeHistory,
  exchangeFortuneItem,
  clearUpdateStatus,
  resetExchangeStatus,
  reset,
  ...internalActions
} = slice.actions;

export const actions = {
  getTodaysFortune,
  updateTodaysFortune,
  animationEnd,
  getExchangeableItems,
  getFortuneExchangeHistory,
  exchangeFortuneItem,
  clearUpdateStatus,
  resetExchangeStatus,
  reset
};

const {
  getTodaysFortuneSucceeded,
  getTodaysFortuneFailed,
  updateTodaysFortuneSucceeded,
  updateTodaysFortuneFailed,
  getExchangeableItemsSucceeded,
  getExchangeableItemsFailed,
  getFortuneExchangeHistorySucceeded,
  getFortuneExchangeHistoryFailed,
  exchangeFortuneItemSucceeded,
  exchangeFortuneItemFailed
} = internalActions;

// Saga

export function* fortuneSaga() {
  yield takeLatest(getTodaysFortune.type, getTodaysFortuneSaga);
  yield takeLatest(updateTodaysFortune.type, updateTodaysFortuneSaga);
  yield takeLatest(getExchangeableItems.type, getFortuneExchangeableItemsSaga);
  yield takeLatest(getFortuneExchangeHistory.type, getFortuneExchangeHistorySaga);
  yield takeLatest(exchangeFortuneItem.type, exchangeFortuneItemSaga);
}

function* getTodaysFortuneSaga() {
  const userState: UserState = yield select((state: RootState) => state.user);
  const { users, selectedUserId, language } = userState;
  const selectedUser = users.find(u => u.id === selectedUserId);

  try {
    if (!selectedUser || !language) {
      // 復帰不可能のエラー
      throw new Error();
    }

    const baid: string = yield call(getFreshToken);

    try {
      const unixTime = Math.floor(Date.now() / 1000);
      const date = formatDate(unixTime);
      const [userProfile, fortuneRes]: [UserProfile, TodaysFortuneResponse] = yield all([
        call(api.getUserProfile, selectedUser.id, language, baid),
        call(api.getTodaysFortune, date, baid)
      ]);

      const pointFactor = fortuneRes.point_factor;
      const currentPoint = fortuneRes.current_point;
      if (!fortuneRes.today) {
        // 今日の占い結果がない場合
        yield put(
          getTodaysFortuneSucceeded({
            pointFactor,
            currentPoint,
            userProfile
          })
        );
      } else {
        const { base, number } = fortuneRes.today;
        const commentId = getCommentId(number);
        yield put(
          getTodaysFortuneSucceeded({
            today: { point: base, commentId },
            pointFactor,
            currentPoint,
            userProfile
          })
        );
      }
    } catch (error) {
      if (error.response?.data?.code === '1102') {
        // プロフィール情報が削除されている場合は所持ポイントだけを取得する
        const { current_point } = yield call(api.getFortunePoint, baid);
        yield put(getTodaysFortuneSucceeded({ currentPoint: current_point }));
      } else {
        throw error;
      }
    }
  } catch (error) {
    switch (checkErrorType(error)) {
      case 'server': {
        const { code } = error.response.data;
        yield put(userActions.causeServerError(code));
        break;
      }
      case 'client': {
        if (error.response.data?.code) {
          const { code } = error.response.data;
          const fortunePointError = getFortunePointError(code);
          yield put(getTodaysFortuneFailed(fortunePointError));
        } else {
          yield put(getTodaysFortuneFailed('unknown'));
        }
        break;
      }
      case 'unstable-network':
        yield put(getTodaysFortuneFailed('unstable-network'));
        break;
      default:
        yield put(getTodaysFortuneFailed('unknown'));
    }
  }
}

function* updateTodaysFortuneSaga() {
  try {
    const baid: string = yield call(getFreshToken);
    const date = Math.floor(Date.now() / 1000);
    const formattedDateString = formatDate(date);
    const res: TodaysUpdatedFortuneResponse = yield call(
      api.postTodaysFortune,
      formattedDateString,
      baid
    );
    const pointFactor = res.point_factor;
    const commentId = getCommentId(res.today.number);

    yield put(
      updateTodaysFortuneSucceeded({
        currentPoint: res.current_point,
        pointFactor,
        today: {
          point: res.today.base,
          commentId
        }
      })
    );
  } catch (error) {
    switch (checkErrorType(error)) {
      case 'server': {
        const { code } = error.response.data;
        yield put(userActions.causeServerError(code));
        break;
      }
      case 'client': {
        if (error.response.data?.code) {
          const { code } = error.response.data;
          const fortunePointError = getFortunePointError(code);
          yield put(updateTodaysFortuneFailed(fortunePointError));
        } else {
          yield put(updateTodaysFortuneFailed('unknown'));
        }
        break;
      }
      case 'unstable-network':
        yield put(updateTodaysFortuneFailed('unstable-network'));
        break;
      default:
        yield put(updateTodaysFortuneFailed('unknown'));
    }
  }
}

function* getFortuneExchangeableItemsSaga() {
  try {
    const language = i18n.language;
    const baid: string = yield call(getFreshToken);
    const res: FortuneExchangeableItemsResponse = yield call(
      api.getFortuneExchangeableItems,
      language,
      baid
    );
    yield put(getExchangeableItemsSucceeded(res));
  } catch (error) {
    console.error(error);
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(userActions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(getExchangeableItemsFailed('unstable-network'));
    } else {
      yield put(getExchangeableItemsFailed('loading-failed'));
    }
  }
}

function* getFortuneExchangeHistorySaga() {
  try {
    const language = i18n.language;
    const baid: string = yield call(getFreshToken);
    const res: FortuneExchangeHistoryResponse = yield call(
      api.getFortuneExchangeHistory,
      language,
      baid
    );
    yield put(
      getFortuneExchangeHistorySucceeded({
        totalUsedPoint: res.total_used_point,
        totalGotPoint: res.total_got_point,
        itemHistories: res.item_histories
      })
    );
  } catch (error) {
    console.error(error);
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(userActions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(getFortuneExchangeHistoryFailed('unstable-network'));
    } else {
      yield put(getFortuneExchangeHistoryFailed('loading-failed'));
    }
  }
}

function* exchangeFortuneItemSaga(action: {
  type: typeof exchangeFortuneItem.type;
  payload: {
    unique_id: number;
    remake_id?: number;
  };
}) {
  try {
    const baid: string = yield call(getFreshToken);
    const { unique_id, remake_id } = action.payload;
    const date = Math.floor(Date.now() / 1000);
    const formattedDateString = formatDate(date);
    const res: { current_point: number } = yield call(
      api.exchangeFortuneItem,
      baid,
      formattedDateString,
      unique_id,
      remake_id
    );
    const currentPoint = res.current_point;
    yield put(exchangeFortuneItemSucceeded({ currentPoint }));
  } catch (error) {
    switch (checkErrorType(error)) {
      case 'server': {
        const { code } = error.response.data;
        yield put(userActions.causeServerError(code));
        break;
      }
      case 'client': {
        if (error.response.data?.code) {
          const { code } = error.response.data;
          const fortuneExchangeError = getFortuneExchangeError(code);
          yield put(exchangeFortuneItemFailed(fortuneExchangeError));
        } else {
          yield put(exchangeFortuneItemFailed('unknown'));
        }
        break;
      }
      case 'unstable-network':
        yield put(exchangeFortuneItemFailed('unstable-network'));
        break;
      default:
        yield put(exchangeFortuneItemFailed('unknown'));
    }
  }
}

// helper function
function getFortunePointError(code?: string): FortuneResultErrorType {
  switch (code) {
    case '1501':
    case '1503':
      return 'invalid-date';
    case '1502':
      return 'have-already-got-point';
    default:
      return 'unknown';
  }
}

function getFortuneExchangeError(code?: string): FortuneExchangeErrorType {
  switch (code) {
    case '1601':
      return 'lack-of-points';
    case '1602':
      return 'exchange-invalid-item';
    case '1603':
      return 'exceeded-exchanged-item-storage';
    case '1604':
      return 'invalid-date';
    case '1605':
      return 'exceeded-number-of-changes';
    default:
      return 'unknown';
  }
}
